import { Cause, Effect, Exit, Fiber, Option, Redacted, Ref, Schema } from "effect";
import { workerstationService } from "../../effect/service.ts";
import { privateDependencyFailure } from "../../errors/private-cause.ts";
import { failureReasons } from "../../effect/settlement.ts";

export const RecordingCompressionError = /* @__PURE__ */ (() => {
  class RecordingCompressionError extends privateDependencyFailure<RecordingCompressionError>()("RecordingCompressionError", "recording compression") {}
  return RecordingCompressionError;
})();
export type RecordingCompressionError = InstanceType<typeof RecordingCompressionError>;
export const recordingCompressionError = (cause: unknown) => new RecordingCompressionError({ cause: Redacted.make(cause) });
export const CompressionKind = Schema.Literals(["gzip", "gunzip"]);
export type CompressionKind = typeof CompressionKind.Type;
export interface CompressionCarrier {
  readonly readable: ReadableStream<Uint8Array>;
  readonly writable: WritableStream<BufferSource>;
}
export interface CompressionWriter {
  readonly write: (bytes: Uint8Array<ArrayBuffer>) => Effect.Effect<void, RecordingCompressionError>;
  readonly close: Effect.Effect<void, RecordingCompressionError>;
  readonly abort: Effect.Effect<void, RecordingCompressionError>;
  readonly release: Effect.Effect<void, RecordingCompressionError>;
}
export interface CompressionReader {
  readonly read: Effect.Effect<Option.Option<Uint8Array>, RecordingCompressionError>;
  readonly cancel: Effect.Effect<void, RecordingCompressionError>;
  readonly release: Effect.Effect<void, RecordingCompressionError>;
}
export class RecordingCompressionIo extends workerstationService<RecordingCompressionIo, {
  readonly open: (kind: CompressionKind) => Effect.Effect<CompressionCarrier, RecordingCompressionError>;
  readonly cancel: (carrier: CompressionCarrier) => Effect.Effect<void, RecordingCompressionError>;
  readonly writer: (carrier: CompressionCarrier) => Effect.Effect<CompressionWriter, RecordingCompressionError>;
  readonly reader: (carrier: CompressionCarrier) => Effect.Effect<CompressionReader, RecordingCompressionError>;
}>()("RecordingCompressionIo") {}

const settle = <A>(result: Exit.Exit<A, RecordingCompressionError>, ...others: readonly Exit.Exit<unknown, RecordingCompressionError>[]) => {
  const reasons = failureReasons(result, ...others);
  return reasons.length ? Exit.failCause(Cause.fromReasons(reasons)) : result;
};
const own = <Handle, A, R>(acquire: Effect.Effect<Handle, RecordingCompressionError, R>,
  use: (handle: Handle) => Effect.Effect<A, RecordingCompressionError, R>, release: (handle: Handle, result: Exit.Exit<A, RecordingCompressionError>) => Effect.Effect<void, RecordingCompressionError, R>) => Effect.gen(function* () {
  const handle = yield* acquire;
  const result = yield* Effect.exit(use(handle));
  const closed = yield* Effect.exit(release(handle, result));
  return yield* settle(result, closed);
});
const collect = (reader: CompressionReader) => Effect.gen(function* () {
  const chunks = yield* Ref.make<readonly Uint8Array[]>([]);
  for (;;) {
    const next = yield* reader.read;
    if (Option.isNone(next)) break;
    yield* Ref.update(chunks, values => [...values, next.value]);
  }
  const values = yield* Ref.get(chunks);
  return yield* Effect.try({ try: () => {
    const output = new Uint8Array(values.reduce((total, chunk) => total + chunk.byteLength, 0));
    let offset = 0;
    for (const chunk of values) { output.set(chunk, offset); offset += chunk.byteLength; }
    return output;
  }, catch: recordingCompressionError });
});

/** Native hosts may preserve their synchronous constructor boundary with this operation. */
export const openCompression = Effect.fn("Recording.openCompression")(function* (kind: CompressionKind) {
  return yield* (yield* RecordingCompressionIo).open(kind);
});

/** One accepted finite transform owns both carriers until their settlement and release. */
export const compressWithCarrier = Effect.fn("Recording.compressWithCarrier")(function* (carrier: CompressionCarrier, bytes: Uint8Array) {
  const io = yield* RecordingCompressionIo;
  const aborted = yield* Ref.make(false);
  const abort = (writer: CompressionWriter) => Effect.gen(function* () {
    if (!(yield* Ref.getAndSet(aborted, true))) yield* writer.abort;
  });
  return yield* own(io.writer(carrier), writer => Effect.gen(function* () {
    const snapshot = yield* Effect.try({ try: () => bytes.slice(), catch: recordingCompressionError });
    const writing = yield* Effect.forkChild(writer.write(snapshot).pipe(Effect.andThen(writer.close), Effect.uninterruptible), { startImmediately: true });
    const immediate = yield* Effect.sync(() => writing.pollUnsafe());
    if (immediate && Exit.isFailure(immediate)) return yield* Effect.failCause(immediate.cause);
    const acquired = yield* Effect.exit(io.reader(carrier));
    if (Exit.isFailure(acquired)) {
      const aborting = yield* Effect.forkChild(Effect.exit(abort(writer)), { startImmediately: true, uninterruptible: true });
      const discarded = yield* Effect.exit(io.cancel(carrier));
      const wrote = yield* Fiber.await(writing), cancelled = yield* Fiber.join(aborting);
      return yield* settle(Exit.failCause(acquired.cause), wrote, discarded, cancelled);
    }
    return yield* own(Effect.succeed(acquired.value), reader => Effect.gen(function* () {
      const draining = yield* Effect.forkChild(collect(reader).pipe(Effect.uninterruptible), { startImmediately: true });
      const failed = yield* Effect.raceFirst(Fiber.await(draining).pipe(Effect.map(Exit.isFailure), Effect.interruptible), Fiber.await(writing).pipe(Effect.map(Exit.isFailure), Effect.interruptible));
      const cleanup = failed ? yield* Effect.gen(function* () {
        // Start both cancellation operations before joining either native carrier.
        const aborting = yield* Effect.forkChild(Effect.exit(abort(writer)), { startImmediately: true, uninterruptible: true });
        const cancelled = yield* Effect.exit(reader.cancel);
        return [cancelled, yield* Fiber.join(aborting)] as const;
      }) : [];
      const read = yield* Fiber.await(draining), wrote = yield* Fiber.await(writing);
      // The original read refusal takes precedence over a simultaneous write refusal.
      return yield* settle(read, wrote, ...cleanup);
    }), reader => reader.release);
  }), (writer, result) => Effect.gen(function* () {
    const cancelled = Exit.isFailure(result) ? yield* Effect.exit(abort(writer)) : Exit.succeed(undefined);
    const released = yield* Effect.exit(writer.release);
    return yield* settle(cancelled, released);
  })).pipe(Effect.uninterruptible);
});
export const gzipSegmentBytes = Effect.fn("Recording.gzipSegmentBytes")(function* (bytes: Uint8Array) {
  return yield* compressWithCarrier(yield* openCompression("gzip"), bytes);
});
export const gunzipSegmentBytes = Effect.fn("Recording.gunzipSegmentBytes")(function* (bytes: Uint8Array) {
  return yield* compressWithCarrier(yield* openCompression("gunzip"), bytes);
});
